import React from 'react';
import styled from 'styled-components';
import {
  Button,
  ContentList,
  Icon,
  Text,
  Page,
  SearchBar,
  Tabs,
  TabPane,
  Tooltip,
} from 'components';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import NotificationFilters from 'components/AppHeader/NotificationFilters';
import sanitizeHtml from 'sanitize-html';
import { formatDate } from 'utils';
import { useBreakpoint, useDebounce } from 'hooks';
import axios from 'axios';
import type { JsonObject, NotificationSearchResponse } from '@/app-package';
import useNotification from './useNotification';
import NotificationDetail from './NotificationDetail';
import { SET_SEARCH_TERM, SET_SEARCH_DELETED } from './actions';

type Notification = JsonObject<
  NotificationSearchResponse['notifications'][number]
>;

// Max height for IE Users
const StyledWrappedContainer = styled.div`
  span {
    max-height: 66px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;
const NotificationCentrePage: React.FC = () => {
  const [showAdvanceSearch, setShowAdvanceSearch] = React.useState(false);
  const [notificationDetail, setNotificationDetail] = React.useState<any>(null);
  const [viewDeleted, setViewDeleted] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebounce(searchTerm);

  const {
    notifications,
    isLoading,
    filtersCheckboxProps,
    getNotificationType,
    getAvatarComponent,
    onLoadMore,
    dispatch,
    markAllRead,
    hasMore,
    deleteRestoreNotification,
  } = useNotification();

  React.useEffect(() => {
    dispatch({ type: SET_SEARCH_TERM, payload: debouncedSearchTerm });
  }, [debouncedSearchTerm]);

  const getBody = ({ body }: Notification) => sanitizeHtml(body, { allowedTags: [], allowedAttributes: {} });

  const getDateComponent = (date: string) => (
      <Tooltip title={moment(date).format('llll')} placement="bottom">
        {formatDate(date)}
      </Tooltip>
    );

  const getHeader = (notification: Notification) => {
    if (notification.type.includes('appointment')) {
      return notification.notificationType === 'coop'
        ? 'Showing you have requested'
        : 'Showing on your listing';
    }
    if (notification.type.includes('offer')) {
      if (notification.type.includes('Delete')) {
        return notification.type.includes('Coop')
          ? 'Offer registration updated'
          : 'Offer modified on your listing';
      }
      return notification.type.includes('Coop')
        ? 'Offer registration updated'
        : 'Offer registered on your listing';
    }
    if (notification.type.includes('feedback')) {
      return notification.notificationType === 'coop'
        ? 'Provide feedback on your showing'
        : 'Feedback submitted on your listing';
    }
    if (notification.type.includes('transfer')) {
      return 'Transfer Request Update';
    }
    if (notification.type === 'adminBlast') return 'Brokerage Announcement';
    if (notification.type === 'listingExpiryReminder')
      return 'Listing Expiry Reminder';
    if (notification.type === 'virtualTourConfirmed')
      return notification.notificationType === 'coop'
        ? 'Virtual showing you have requested'
        : 'Virtual showing on your listing';
    if (notification.type === 'propertyPriceChange')
      return 'Price Change Update';
    if (notification.type === 'propertyStatusChange')
      return 'Status Change Update';
    if (notification.type === 'manualCoopAdded')
      return 'Your Key Has Been Added';
    if (notification.type === 'manualCoopRemoved')
      return 'Your Key Has Been Removed';
    return 'Property Announcement';
  };

  const history = useHistory();

  const { notificationId } = useParams<{
    notificationId: string | undefined;
  }>();

  React.useEffect(() => {
    (async () => {
      if (!notificationId) {
        setNotificationDetail(null);
        return;
      }
      const { data } = await axios.get<{ data: any }>(
        `/notification/${notificationId}/details`,
      );
      setNotificationDetail(data);
    })();
  }, [notificationId]);

  const LoadMoreComponent = React.useMemo(
    () => (
      <div style={{ textAlign: 'center' }}>
        <Button onClick={onLoadMore} disabled={!hasMore}>
          {hasMore ? 'Load More' : 'No More Notifications'}
        </Button>
      </div>
    ),
    [hasMore, onLoadMore],
  );

  const items = React.useMemo(
    () =>
      notifications?.map(notification => {
        const { subject, createdAt, id, isDirty, isDeleted, appointment } =
          notification;
        const type = getNotificationType(notification);
        const AvatarComponent = getAvatarComponent(type, notification);
        const DateComponent = getDateComponent(createdAt);
        const body = getBody(notification);
        const header = getHeader(notification);
        const op = isDeleted ? 'undo' : 'delete';
        const onOpClick: React.MouseEventHandler = e => {
          e.stopPropagation();
          deleteRestoreNotification(op, id);
        };
        return {
          onItemClick: () => {
            history.push(`/notification/list/${id}`);
          },
          AvatarComponent,
          DateComponent,
          MainComponent: (
            <div>
              <div>
                <Text strong>{header}</Text>
              </div>
              <Text>{subject}</Text>
              <StyledWrappedContainer>
                <Text>{body}</Text>
              </StyledWrappedContainer>
            </div>
          ),
          OpComponent: (
            <Button type="text" icon={<Icon type={op} />} onClick={onOpClick} />
          ),
          date: createdAt,
          showBadge: isDirty,
          selected: id === notificationId,
          appointmentId: appointment,
          notificationId: id,
        };
      }) || [],
    [notifications, notificationId],
  );

  const markAllAsReadBtn = () => (
    <span key="read-all">
      &nbsp; &nbsp;
      <Tooltip title="Mark All as Read">
        <Button
          key="allRead"
          onClick={markAllRead}
          aria-label="Mark All as Read"
          shape="circle"
        >
          <Icon type="check" width={15} color="info" />
        </Button>
      </Tooltip>
    </span>
  );

  const { md, lg, xxl } = useBreakpoint();

  const asideWidth = React.useMemo(() => {
    if (xxl) return '600px';
    if (lg) return '420px';
    if (md) return '310px';
    return '100%';
  }, [xxl, lg, md]);

  const getAside = () => {
    if (!md && notificationId) return null;
    return (
      <>
        <SearchBar
          value={searchTerm}
          placeholder="Search notifications"
          onChange={setSearchTerm}
          shape="round"
          loading={isLoading}
        />
        <Tabs
          size="large"
          defaultActiveKey="inbox"
          onChange={key => {
            setShowAdvanceSearch(false);
            setViewDeleted(key === 'delete');
            setSearchTerm('');
            dispatch({ type: SET_SEARCH_DELETED, payload: key === 'delete' });
          }}
          tabBarExtraContent={{
            right: [
              <Button
                key="search"
                onClick={() => setShowAdvanceSearch(prev => !prev)}
              >
                Advanced Search
              </Button>,
              viewDeleted ? null : markAllAsReadBtn(),
            ],
          }}
        >
          <TabPane tab="Inbox" key="inbox">
            {!viewDeleted && (
              <>
                {showAdvanceSearch && (
                  <NotificationFilters
                    filtersCheckboxProps={filtersCheckboxProps}
                  />
                )}
                <ContentList
                  isLoading={isLoading}
                  // @ts-expect-error: type mismatch between id and appointment object
                  items={items}
                  emptyDescription="No notifications found"
                  loadMore={LoadMoreComponent}
                />
              </>
            )}
          </TabPane>
          <TabPane tab="Deleted" key="delete">
            {viewDeleted && (
              <>
                {showAdvanceSearch && (
                  <NotificationFilters
                    filtersCheckboxProps={filtersCheckboxProps}
                  />
                )}
                <ContentList
                  isLoading={isLoading}
                  // @ts-expect-error: type mismatch between id and appointment object
                  items={items}
                  emptyDescription="No notifications found"
                  loadMore={LoadMoreComponent}
                />
              </>
            )}
          </TabPane>
        </Tabs>
      </>
    );
  };

  return (
    <Page
      title="Notification Centre"
      asideWidth={asideWidth}
      asideType="filled"
      type="hollow"
      fillScreenHeight
      aside={getAside()}
      showBackButton={Boolean(!md && notificationId)}
    >
      {(md || (!md && notificationId)) && (
        <NotificationDetail
          notification={notificationDetail}
          body={notificationDetail?.body || ''}
        />
      )}
    </Page>
  );
};

export default NotificationCentrePage;
